import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, ChevronLeft, ChevronRight, Plus, Check, X, CheckCheck,
  CalendarDays, Clock, User, Ban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const WD = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const iso = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayIso = () => iso(new Date());

const SLOT_STYLE = {
  free: "border-emerald-200 bg-emerald-50/60 hover:border-emerald-400 hover:bg-emerald-50",
  partial: "border-amber-200 bg-amber-50/60",
  full: "border-red-200 bg-red-50/50",
  past: "border-border bg-secondary/40 opacity-60",
  blocked: "border-border bg-secondary/60 opacity-70",
};
const RES_STATUS = {
  pending: { label: "Bekliyor", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Onaylı", cls: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Tamamlandı", cls: "bg-blue-100 text-blue-700" },
};

export default function Takvim() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(todayIso());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [dialog, setDialog] = useState(null); // { time }
  const [form, setForm] = useState({ service_id: "", staff_id: "", customer_name: "", customer_phone: "" });
  const [busy, setBusy] = useState(false);

  const { data: allRes = [] } = useQuery({ queryKey: ["reservations"], queryFn: () => api.get("/reservations").then((r) => r.data) });
  const { data: agenda, isLoading } = useQuery({
    queryKey: ["agenda", selected],
    queryFn: () => api.get("/reservations/agenda", { params: { date: selected } }).then((r) => r.data),
  });

  // summary counts
  const summary = useMemo(() => {
    const t = todayIso();
    const now = new Date(); const wd = (now.getDay() + 6) % 7;
    const ws = new Date(now); ws.setDate(now.getDate() - wd); ws.setHours(0, 0, 0, 0);
    const we = new Date(ws); we.setDate(ws.getDate() + 7);
    let today = 0, week = 0, pending = 0, confirmed = 0;
    for (const r of allRes) {
      if (r.status === "cancelled") continue;
      if (r.date === t) today++;
      const rd = new Date(r.date + "T00:00:00");
      if (rd >= ws && rd < we) week++;
      if (r.status === "pending") pending++;
      if (r.status === "confirmed") confirmed++;
    }
    return { today, week, pending, confirmed };
  }, [allRes]);

  // per-day dots for the visible month
  const dayInfo = useMemo(() => {
    const map = {};
    for (const r of allRes) {
      if (r.status === "cancelled") continue;
      const e = map[r.date] || (map[r.date] = { pending: 0, active: 0 });
      if (r.status === "pending") e.pending++; else e.active++;
    }
    return map;
  }, [allRes]);

  const monthGrid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startWd = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startWd; i++) days.push(null);
    const dim = new Date(cursor.y, cursor.m + 1, 0).getDate();
    for (let d = 1; d <= dim; d++) days.push(new Date(cursor.y, cursor.m, d));
    return days;
  }, [cursor]);

  const shiftMonth = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  const openAdd = (time) => {
    const svc = agenda?.services?.[0];
    setForm({ service_id: svc ? svc.id : "", staff_id: "", customer_name: "", customer_phone: "" });
    setDialog({ time });
  };

  const submitManual = async () => {
    if (!form.service_id) { toast.error("Hizmet seçin"); return; }
    if (!form.customer_name.trim()) { toast.error("Müşteri adı girin"); return; }
    setBusy(true);
    try {
      await api.post("/reservations/manual", {
        service_id: form.service_id, date: selected, time: dialog.time,
        customer_name: form.customer_name.trim(), customer_phone: form.customer_phone.trim(),
        ...(form.staff_id ? { staff_id: form.staff_id } : {}),
      });
      qc.invalidateQueries({ queryKey: ["agenda", selected] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Randevu eklendi");
      setDialog(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}`, { status });
      qc.invalidateQueries({ queryKey: ["agenda", selected] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Durum güncellendi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const selDate = new Date(selected + "T00:00:00");
  const cards = [
    { key: "today", label: "Bugün", val: summary.today, cls: "bg-card border-border" },
    { key: "week", label: "Bu Hafta", val: summary.week, cls: "bg-card border-border" },
    { key: "pending", label: "Bekleyen", val: summary.pending, cls: "bg-amber-50 border-amber-200" },
    { key: "confirmed", label: "Onaylı", val: summary.confirmed, cls: "bg-emerald-50 border-emerald-200" },
  ];

  return (
    <div className="space-y-6" data-testid="takvim-page">
      <div>
        <h1 className="text-3xl font-bold">Takvim</h1>
        <p className="text-muted-foreground mt-1">Günlük doluluğu görün, boş slotlara manuel randevu ekleyin</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.key} className={cn("rounded-xl border p-4", c.cls)} data-testid={`summary-${c.key}`}>
            <p className="text-2xl font-bold">{c.val}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Month calendar */}
        <div className="rounded-2xl border border-border bg-card p-4 h-fit" data-testid="month-calendar">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded-lg hover:bg-secondary" data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-semibold">{MONTHS[cursor.m]} {cursor.y}</span>
            <button onClick={() => shiftMonth(1)} className="p-1.5 rounded-lg hover:bg-secondary" data-testid="month-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-1">
            {WD.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthGrid.map((d, i) => {
              if (!d) return <div key={i} />;
              const ds = iso(d);
              const info = dayInfo[ds];
              const isSel = ds === selected;
              const isToday = ds === todayIso();
              return (
                <button key={i} onClick={() => setSelected(ds)} data-testid={`day-${ds}`}
                  className={cn("aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-colors relative",
                    isSel ? "bg-primary text-primary-foreground font-bold" : "hover:bg-secondary",
                    !isSel && isToday && "ring-1 ring-primary/40")}>
                  <span>{d.getDate()}</span>
                  {info && (
                    <span className={cn("absolute bottom-1 w-1.5 h-1.5 rounded-full",
                      isSel ? "bg-primary-foreground" : info.pending > 0 ? "bg-amber-500" : "bg-emerald-500")} />
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Onaylı/aktif</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Bekleyen</span>
          </div>
        </div>

        {/* Daily agenda */}
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="daily-agenda">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="w-5 h-5 text-primary" />
            <div>
              <p className="font-semibold capitalize">
                {selDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" })}
              </p>
              {agenda && !agenda.closed && (
                <p className="text-xs text-muted-foreground">Çalışma saatleri: {agenda.open} - {agenda.close}</p>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
          ) : agenda?.closed ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="agenda-closed">
              <Ban className="w-8 h-8 mx-auto mb-2 opacity-60" /> Bu gün işletme kapalı.
            </div>
          ) : (
            <div className="space-y-2" data-testid="agenda-slots">
              {agenda?.slots?.map((slot) => (
                <div key={slot.time} className={cn("rounded-xl border p-3 flex gap-3", SLOT_STYLE[slot.status])} data-testid={`slot-${slot.time}`}>
                  <div className="w-14 shrink-0 flex flex-col items-center pt-0.5">
                    <span className="text-sm font-bold flex items-center gap-1"><Clock className="w-3 h-3 text-muted-foreground" />{slot.time}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    {slot.reservations.length > 0 ? (
                      <div className="space-y-2">
                        {slot.reservations.map((r) => {
                          const rs = RES_STATUS[r.status] || RES_STATUS.pending;
                          return (
                            <div key={r.id} className="rounded-lg bg-card border border-border p-3" data-testid={`res-card-${r.id}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-semibold truncate">{r.customer_name}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {r.service_name}{r.staff_name ? ` · ${r.staff_name}` : ""} · {money(r.service_price)}
                                  </p>
                                </div>
                                <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0", rs.cls)}>{rs.label}</span>
                              </div>
                              {r.customer_phone && <p className="text-xs text-muted-foreground mt-1">{r.customer_phone}</p>}
                              <div className="flex gap-2 mt-2">
                                {r.status === "pending" && (
                                  <Button size="sm" className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => changeStatus(r.id, "confirmed")} data-testid={`approve-${r.id}`}>
                                    <Check className="w-3.5 h-3.5 mr-1" /> Onayla
                                  </Button>
                                )}
                                {r.status === "confirmed" && (
                                  <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => changeStatus(r.id, "completed")} data-testid={`complete-${r.id}`}>
                                    <CheckCheck className="w-3.5 h-3.5 mr-1" /> Tamamla
                                  </Button>
                                )}
                                {(r.status === "pending" || r.status === "confirmed") && (
                                  <Button size="sm" variant="outline" className="h-8 rounded-full text-destructive border-destructive/30 hover:bg-red-50" onClick={() => changeStatus(r.id, "cancelled")} data-testid={`reject-${r.id}`}>
                                    <X className="w-3.5 h-3.5 mr-1" /> İptal
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {(slot.status === "partial") && (
                          <button onClick={() => openAdd(slot.time)} className="text-xs font-semibold text-primary hover:underline flex items-center gap-1" data-testid={`add-more-${slot.time}`}>
                            <Plus className="w-3.5 h-3.5" /> Bu saate ekle
                          </button>
                        )}
                      </div>
                    ) : slot.status === "free" ? (
                      <button onClick={() => openAdd(slot.time)} className="w-full flex items-center justify-between group" data-testid={`free-slot-${slot.time}`}>
                        <span className="text-sm font-medium text-emerald-700">Müsait</span>
                        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Plus className="w-4 h-4" />
                        </span>
                      </button>
                    ) : slot.status === "blocked" ? (
                      <span className="text-sm text-muted-foreground">Kapalı</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">{slot.status === "past" ? "Geçti" : "—"}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manual booking dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="manual-booking-dialog">
          <DialogHeader>
            <DialogTitle>Manuel Randevu · {dialog?.time}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Hizmet</label>
              <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
                <SelectTrigger className="h-11 mt-1" data-testid="manual-service"><SelectValue placeholder="Hizmet seçin" /></SelectTrigger>
                <SelectContent>
                  {agenda?.services?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} · {money(s.price)} · {s.duration_minutes} dk</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {agenda?.staff?.length > 0 && (
              <div>
                <label className="text-sm font-medium flex items-center gap-1"><User className="w-3.5 h-3.5" /> Personel</label>
                <Select value={form.staff_id || "any"} onValueChange={(v) => setForm((f) => ({ ...f, staff_id: v === "any" ? "" : v }))}>
                  <SelectTrigger className="h-11 mt-1" data-testid="manual-staff"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Farketmez</SelectItem>
                    {agenda.staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Müşteri Adı</label>
              <Input className="h-11 mt-1" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="Ad Soyad" data-testid="manual-name" />
            </div>
            <div>
              <label className="text-sm font-medium">Telefon (opsiyonel)</label>
              <Input className="h-11 mt-1" value={form.customer_phone} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))} placeholder="05xx xxx xx xx" data-testid="manual-phone" />
              <p className="text-xs text-muted-foreground mt-1">Telefon girilirse müşteriye WhatsApp onay mesajı gönderilir.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)} data-testid="manual-cancel">Vazgeç</Button>
            <Button className="rounded-full" disabled={busy} onClick={submitManual} data-testid="manual-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Randevu Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
